import useUrlState from "@ahooksjs/use-url-state";
import { Box, Container } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CachePolicies } from "use-http";

import { ErrorView } from "components/common/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { ExpirePlanBar } from "components/common/ExpirePlanBar/ExpirePlanBar";
import { SubNavBar } from "components/common/SubNavBar";
import { TerminationNoticeBar } from "components/common/TerminationNoticeBar/TerminationNoticeBar";
import { OnboardingModal } from "components/onboarding/OnboardingModal/OnboardingModal";
import { JANZU_URL } from "config/config";
import { DetectorsList } from "features/detectorList/DetectorsList";
import { FilterDetector } from "features/detectorList/filterDetector/FilterDetector";
import { SelectDetectorFilter } from "features/detectorList/selectDetectorFilter/SelectDetectorFilter";
import { DetectorManagementOpenModal, getDetectorManagementOpenModal } from "features/detectorList/utils";
import { IDPMigrationModal } from "features/idpMigration/IDPMigrationModal";
import { useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useIsAdminBrowsingBlocked } from "hooks/useIsAdminBrowsingBlocked/useIsAdminBrowsingBlocked";
import { useIsUserSciduck } from "hooks/useIsUserSciduck/useIsUserSciduck";
import { getUserState } from "store/slices/Auth/authSlice";
import { ListResponse } from "types/api";
import { DetectorBase } from "types/detectors";
import { User } from "types/users";
import { extractPaginateFrom } from "utils/utilityFunctions";

export type FilterState = Partial<{
  [key: string]: any;
}>;

export interface FilterDetectorState {
  search: string;
  actives: string;
  mode: string;
  group_id: string;
  group_name: string;
  sort: string;
  ordering: string;
}

/**
 * Entrypoint for listing detectors.
 */
export function DetectorManagement() {
  const isAdminBrowsingBlocked = useIsAdminBrowsingBlocked();

  if (isAdminBrowsingBlocked) {
    return <ErrorView error={ErrorType.ADMIN_DETECTOR_LIST_BLOCKED} />;
  }

  return <DetectorManagementContent />;
}

function DetectorManagementContent() {
  const { state } = useLocation();

  const defaultFilterState: FilterDetectorState = {
    // this stores the state of the page in the url
    search: "",
    actives: "",
    mode: "all",
    group_id: "",
    group_name: "All",
    sort: "-",
    ordering: "recent_used",
  };

  // this stores the state of the page in the url
  const [filterState, setFilterState] = useUrlState<FilterDetectorState>(state || defaultFilterState);

  const [openModal, setOpenModal] = useState<DetectorManagementOpenModal>(null);
  const [dataList, setDataList] = useState<DetectorBase[]>([]);
  const [next, setNext] = useState("");
  const [numDetectors, setNumDetectors] = useState<number | null>(null);

  const user: User = useAppSelector(getUserState);
  const isUserSciDuck = useIsUserSciduck();

  const DEFAULT_PAGE_SIZE = 5;

  const detectorsUrl = useMemo((): string => {
    const paginateFrom = next ? extractPaginateFrom(next) : null;
    const baseDetectorsUrl = `${JANZU_URL}reef-api/detectors`;

    const params = new URLSearchParams();
    params.append("serializer_type", "base");
    params.append("page_size", DEFAULT_PAGE_SIZE.toString());
    params.append("ordering", `${filterState.sort}${filterState.ordering}`);
    if (filterState.search) {
      params.append("search", filterState.search);
    }
    if (filterState.group_id) {
      params.append("group_id", filterState.group_id.toString());
    }
    if (filterState.actives) {
      params.append("actives", filterState.actives.toString());
    }
    if (filterState.mode) {
      const mode = filterState.mode === "all" ? "" : filterState.mode;
      if (mode) params.append("mode", mode.toString());
    }
    if (paginateFrom) {
      params.append("paginate_from", paginateFrom);
    }
    return `${baseDetectorsUrl}?${params.toString()}`;
  }, [next, filterState]);

  // Get list data from API
  const {
    get: getDataListDetectors,
    data: dataListDetectors,
    error: errorListDetectors,
    loading: loadingListDetectors,
  } = useFetchCustom<ListResponse<DetectorBase>>(detectorsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  // Intersection observer
  const fetchMore = useCallback(
    (entries) => {
      if (loadingListDetectors) return;

      if (entries && entries[0]) {
        if (entries[0].isIntersecting && dataListDetectors?.next) {
          setNext(dataListDetectors.next);
        }
      }
    },
    [dataListDetectors?.next, loadingListDetectors]
  );

  // set number of detector based on detectors fetched
  useEffect(() => {
    if (!errorListDetectors && dataListDetectors) {
      setNumDetectors(dataListDetectors.count);
    }
  }, [next, dataListDetectors, errorListDetectors]);

  // Fetch detector data and store it on redux
  useEffect(() => {
    const getData = () => {
      getDataListDetectors().then((response) => {
        if (response && response.results) {
          setDataList((dataList) => [...dataList, ...response.results]);
        }
      });
    };

    if ((next || dataList?.length === 0) && !loadingListDetectors) {
      getData();
    }
    // eslint-disable-next-line
  }, [next]);

  useEffect(() => {
    if (isUserSciDuck) {
      return;
    }

    const newOpenModal = getDetectorManagementOpenModal(user);
    setOpenModal(newOpenModal);
  }, [user, isUserSciDuck]);

  const handleChangeFilterState = (state: Partial<FilterDetectorState>) => {
    setFilterState({
      ...filterState,
      ...state,
    });
  };

  const handleCloseOnboardingModal = () => {
    setOpenModal(null);
  };

  const showLoadingIndicator = dataListDetectors?.next != null;

  return (
    <Box id="detector-management">
      <SubNavBar>
        {numDetectors !== null && (
          <SelectDetectorFilter
            numDetectors={numDetectors}
            valueMenu={filterState.actives}
            setValueMenu={(value: string) =>
              handleChangeFilterState({
                actives: value,
              })
            }
          />
        )}
      </SubNavBar>
      <ExpirePlanBar />
      <TerminationNoticeBar />
      <Container>
        {numDetectors !== null && (
          <FilterDetector
            filterState={filterState}
            handleChangeFilterState={handleChangeFilterState}
            numDetectors={numDetectors}
          />
        )}
        <DetectorsList
          dataList={dataList}
          loadingListDetectors={loadingListDetectors}
          showLoadingIndicator={showLoadingIndicator}
          fetchMore={fetchMore}
        />
      </Container>

      <IDPMigrationModal isOpen={openModal === "migration"} user={user} />

      <OnboardingModal isOpen={openModal === "new-onboarding"} onClose={handleCloseOnboardingModal} />
    </Box>
  );
}
